import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { app_attachments_path } from '@/rails/routes';
import { router } from '@inertiajs/react';
import { useRef, useState, type FormEvent } from 'react';

interface AttachmentUploadProps {
  appId: string;
  inputId: string;
  label: string;
  // False until the app's setup has finished. There is nowhere to keep a file before then.
  provisioned: boolean;
  // Both set when the file belongs to a record's attachments field. Neither for a loose file.
  recordId?: string;
  fieldKey?: string;
}

// Picks one file and sends it. The page reloads in place with the new attachment.
const AttachmentUpload = ({
  appId,
  inputId,
  label,
  provisioned,
  recordId,
  fieldKey,
}: AttachmentUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [percent, setPercent] = useState<number | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const noteId = `${inputId}-note`;

  const upload = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (file && provisioned) {
      const payload =
        recordId && fieldKey ? { file, record_id: recordId, field_key: fieldKey } : { file };

      router.post(app_attachments_path(appId), payload, {
        forceFormData: true,
        preserveScroll: true,
        onStart: () => setUploading(true),
        onProgress: (progress) => setPercent(progress?.percentage ?? null),
        onFinish: () => {
          setUploading(false);
          setPercent(null);
          setFile(null);
          if (input.current) input.current.value = '';
        },
      });
    }
  };

  return (
    <form onSubmit={upload} className="space-y-2">
      <Label htmlFor={inputId}>{label}</Label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          ref={input}
          id={inputId}
          type="file"
          disabled={uploading || !provisioned}
          aria-describedby={provisioned ? undefined : noteId}
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          className="sm:max-w-sm"
        />
        <Button type="submit" variant="outline" disabled={!file || uploading || !provisioned}>
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
      </div>
      {provisioned ? null : (
        <p id={noteId} className="text-muted-foreground text-xs">
          Available when setup finishes
        </p>
      )}
      <p className="text-muted-foreground text-xs" role="status" aria-live="polite">
        {uploading && percent !== null ? `${Math.round(percent)}% uploaded` : ''}
      </p>
    </form>
  );
};

export default AttachmentUpload;
